import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ErrorHandler, LOCALE_ID, NgModule, APP_INITIALIZER } from '@angular/core';
import { RouterModule } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgIdleModule } from '@ng-idle/core';

import localeAu from '@angular/common/locales/en-AU';

import { OAuthModule } from 'angular-oauth2-oidc';

import { OrderModule } from './order/order.module';
import { MessageBoxModule } from './messagebox/messagebox.module';
import { AuthenticationModule } from './authentication/authentication.module';

import { ConfigService } from './services/config.service';
import { FeaturesService } from './services/features.service';
import { JwtInterceptorService } from './services/jwt.interceptor.service';
import { ErrorInterceptorService } from './services/error.interceptor.service';
import { AuthenticationGuardService } from './authentication/services/authentication.guard.service';

import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';

import { AppComponent } from './app.component';
import { AppErrorHandler } from './services/app.error.handler';
import { OnlineStatusHandler } from './services/online.status.handler';
import { LogoutComponent } from './authentication/logout/logout.component';
import { LoginComponent } from './authentication/login/login.component';
import { LoginCallbackComponent } from './authentication/login-callback/login-callback.component';
import { ErrorComponent } from './app-common/error/error.component';
import { AppCommonModule } from './app-common/app-common.module';
import { SharedModule } from './shared/shared.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { MyDetailsComponent } from './my-details/my-details.component';
import { MyHospitalUsersComponent } from './my-hospital-users/my-hospital-users.component';
import { MyHospitalUsersModule } from './my-hospital-users/my-hospital-users.module';
import { MyDetailsModule } from './my-details/my-details.module';
import { registerLocaleData } from '@angular/common';
import { API_BASE_URL as controlCentreApiBaseUrl, ControlCentreApiClient } from './api-module/control-centre-api-client';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faEdit } from '@fortawesome/free-regular-svg-icons';
import {
    faCaretDown,
    faTrash as fasTrash,
    faCommentAlt as fasCommentAlt,
    faCircleXmark,
    faCircleNotch
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL as userManagementBaseUrl, UserManagementApiClient } from './api-module/user-management-api-client-v2';
import { PasswordChangeComponent } from './authentication/password-change/password-change.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { FeatureFlagService } from './services/feature-flag.service';
import { Urls } from './models/urls';
import { WorklistComponent } from './worklist/worklist.component';
import { ReferralsComponent } from './referrals/referrals.component';
import { ClinicalFeedbackComponent } from './clinical-feedback/clinical-feedback.component';
import { ImagingRequestsComponent } from './imaging-requests/imaging-requests.component';
import { provideStore } from '@ngxs/store';
import { WorklistFiltersState } from './worklist/worklist-filters/worklist-filters.state';
import { ReferralFiltersState } from './referrals/referral-filters/referral-filters.state';
import { DatadogService } from './services/datadog.service';

export const DATE_FORMATS = {
    parse: {
        dateInput: 'D/M/YYYY'
    },
    display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM YYYY'
    }
};

export function initializeApp(configService: ConfigService, featureFlagService: FeatureFlagService) {
    return (): Promise<void> =>
        configService.load().then(() => {
            featureFlagService.load();
        });
}

export function initializeFeature(featuresService: FeaturesService) {
    return (): Promise<void> => featuresService.load();
}

export function initializeDatadog(datadogService: DatadogService) {
    return () => datadogService.init();
}

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        RouterModule.forRoot(
            [
                { path: 'error', component: ErrorComponent },
                { path: 'logout', component: LogoutComponent },
                { path: 'login', component: LoginComponent },
                { path: 'login/callback', component: LoginCallbackComponent },
                {
                    path: 'dashboard',
                    component: DashboardComponent,
                    canActivate: [AuthenticationGuardService],
                    children: [
                        {
                            path: 'orders',
                            component: WorklistComponent,
                            canActivate: [AuthenticationGuardService]
                        },
                        {
                            path: 'referrals',
                            component: ReferralsComponent,
                            canActivate: [AuthenticationGuardService]
                        },
                        {
                            path: 'clinical-feedback',
                            component: ClinicalFeedbackComponent,
                            canActivate: [AuthenticationGuardService]
                        },
                        {
                            path: 'imaging-requests',
                            component: ImagingRequestsComponent,
                            canActivate: [AuthenticationGuardService]
                        },
                        {
                            path: 'demand-management',
                            loadChildren: () =>
                                import('./demand-management/demand-management.module').then(mod => mod.DemandManagementModule),
                            canActivate: [AuthenticationGuardService]
                        }
                    ]
                },
                {
                    path: 'orders/worklist',
                    redirectTo: Urls.DASHBOARD
                },
                {
                    path: 'users/my-details',
                    component: MyDetailsComponent,
                    canActivate: [AuthenticationGuardService]
                },
                {
                    path: 'users/my-hospital-users',
                    component: MyHospitalUsersComponent,
                    canActivate: [AuthenticationGuardService]
                },
                // TODO -> sort out the password-change and /users/ prefix
                {
                    path: 'users/password-change',
                    component: PasswordChangeComponent,
                    canActivate: [AuthenticationGuardService]
                },
                { path: '', redirectTo: Urls.DASHBOARD, pathMatch: 'full' },
                // otherwise redirect to home
                { path: '**', redirectTo: Urls.DASHBOARD }
            ],
            {}
        ),
        OAuthModule.forRoot(),
        NgIdleModule.forRoot(),
        DashboardModule,
        OrderModule,
        AuthenticationModule,
        AppCommonModule,
        MessageBoxModule,
        SharedModule.forRoot(),
        MyHospitalUsersModule,
        MyDetailsModule
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            deps: [ConfigService, FeatureFlagService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeFeature,
            deps: [FeaturesService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeDatadog,
            deps: [DatadogService],
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: JwtInterceptorService,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptorService,
            multi: true
        },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
        {
            provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
            useValue: { strict: true }
        },
        { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
        { provide: ErrorHandler, useClass: AppErrorHandler },
        { provide: LOCALE_ID, useValue: 'en-AU' },
        OnlineStatusHandler,
        ControlCentreApiClient,
        {
            provide: controlCentreApiBaseUrl,
            useFactory: ConfigService.getBaseApiUrl
        },
        UserManagementApiClient,
        {
            provide: userManagementBaseUrl,
            useFactory: ConfigService.getBaseApiUrl
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideStore([WorklistFiltersState, ReferralFiltersState])
    ]
})
export class AppModule {
    constructor(library: FaIconLibrary) {
        // TODO: Add more icons here to be used anywhere in the project
        library.addIcons(faEdit);
        library.addIcons(faCaretDown);
        library.addIcons(fasTrash);
        library.addIcons(fasCommentAlt);
        library.addIcons(faCircleXmark);
        library.addIcons(faCircleNotch);
    }
}

registerLocaleData(localeAu);
